import { isBoolean } from "es-toolkit";

import type { BooleanCondition, BooleanOperator } from "@skilitics/judy";
import { isReferencedValue, type ValueConfigFor } from "@skilitics/nautilus-core-designer-data";

import type { ConditionBuilder, ConditionBuilderContext } from "../../types";
import { ValueConfigSummaryText } from "../../values/forms/ValueConfigSummaryText.tsx";
import type { BooleanConditionFormValues } from "./forms";
import { BooleanConditionForm } from "./forms";
import { isBooleanOperator, operatorToEnglish } from "./helpers";
import { ConditionBuilderSummary } from "./ui/Summary";

export interface BooleanConditionContext extends ConditionBuilderContext {
  config: {
    operator?: BooleanOperator;
    value?: ValueConfigFor<"boolean">;
  };
  spec: {
    property: string;
  };

  // the assumption is that any property referenced by consuming node designer is a boolean
  result: Record<string, boolean>;
}

export type BooleanConditionBuilder = typeof booleanConditionBuilder;

// generic so asks for a property name in spec
export const booleanConditionBuilder: ConditionBuilder<BooleanConditionContext> = {
  typeId: "boolean",
  defaultCategory: "Variables",
  build: ({ config, spec: { property }, resultProperty, resolvableValue }) => {
    if (!booleanConditionBuilder.isValid?.(config)) return;

    const { operator, value } = config;

    const object = resolvableValue(value, "boolean");
    if (object === undefined) return;

    return {
      type: "boolean",
      subject: resultProperty(property, "boolean"),
      operator,
      object,
    } as BooleanCondition;
  },
  isValid: ({ operator, value }) => isBooleanOperator(operator) && (isBoolean(value) || isReferencedValue(value)),
  getInitialConfig: () => ({}),
  views: {
    summary: ({ spec: { title }, config: { operator, value } }) => (
      <ConditionBuilderSummary
        title={title}
        operator={operatorToEnglish(operator)}
        value={<ValueConfigSummaryText value={value} />}
      />
    ),
    edit: ({ spec, config: { operator, value }, onConfigChange }) => {
      const initialValues: BooleanConditionFormValues = { operator, value: value ?? false };

      return <BooleanConditionForm {...{ spec, initialValues, onSubmit: onConfigChange }} />;
    },
  },
};
